import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Chat, Message, Project, WorkspaceSettings } from '@/lib/workspace/types';

// ============================================================================
// Unified IndexedDB database for OpenDork.
// ============================================================================

const DB_NAME = 'opendork';
/** v2: projects / chats / messages / settings workspace schema */
const DB_VERSION = 2;

export const STORE = {
  NIP05: 'nip05',
  PROFILES: 'profiles',
  SETTINGS: 'settings',
  PROJECTS: 'projects',
  CHATS: 'chats',
  MESSAGES: 'messages',
} as const;

interface OpenDorkDB extends DBSchema {
  nip05: {
    key: string;
    value: unknown;
  };
  profiles: {
    key: string;
    value: unknown;
  };
  settings: {
    key: string;
    value: WorkspaceSettings | string | unknown;
  };
  projects: {
    key: string;
    value: Project;
    indexes: { 'by-updated': number };
  };
  chats: {
    key: string;
    value: Chat;
    indexes: { 'by-project': string; 'by-updated': number };
  };
  messages: {
    key: string;
    value: Message;
    indexes: { 'by-chat': string; 'by-chat-created': [string, number] };
  };
}

let dbPromise: Promise<IDBPDatabase<OpenDorkDB> | null> | null = null;

/**
 * Open (or reuse) the shared OpenDork database.
 * Returns `null` when IndexedDB is unavailable.
 */
export function openDatabase(): Promise<IDBPDatabase<OpenDorkDB> | null> {
  if (!dbPromise) {
    dbPromise = (async () => {
      try {
        return await openDB<OpenDorkDB>(DB_NAME, DB_VERSION, {
          upgrade(db, oldVersion, _newVersion, transaction) {
            if (!db.objectStoreNames.contains(STORE.NIP05)) {
              db.createObjectStore(STORE.NIP05);
            }
            if (!db.objectStoreNames.contains(STORE.PROFILES)) {
              db.createObjectStore(STORE.PROFILES);
            }

            if (oldVersion < 2) {
              // Legacy v1 had a bare "messages" store without indexes — recreate
              if (db.objectStoreNames.contains(STORE.MESSAGES)) {
                const legacy = transaction.objectStore(STORE.MESSAGES);
                // If it already has our indexes, leave it; otherwise recreate
                try {
                  legacy.index('by-chat');
                } catch {
                  db.deleteObjectStore(STORE.MESSAGES);
                }
              }

              if (!db.objectStoreNames.contains(STORE.SETTINGS)) {
                db.createObjectStore(STORE.SETTINGS);
              }
              if (!db.objectStoreNames.contains(STORE.PROJECTS)) {
                const projects = db.createObjectStore(STORE.PROJECTS, { keyPath: 'id' });
                projects.createIndex('by-updated', 'updatedAt');
              }
              if (!db.objectStoreNames.contains(STORE.CHATS)) {
                const chats = db.createObjectStore(STORE.CHATS, { keyPath: 'id' });
                chats.createIndex('by-project', 'projectId');
                chats.createIndex('by-updated', 'updatedAt');
              }
              if (!db.objectStoreNames.contains(STORE.MESSAGES)) {
                const messages = db.createObjectStore(STORE.MESSAGES, { keyPath: 'id' });
                messages.createIndex('by-chat', 'chatId');
                messages.createIndex('by-chat-created', ['chatId', 'createdAt']);
              }
            }
          },
        });
      } catch {
        return null;
      }
    })();
  }
  return dbPromise;
}

/** Reset cached promise (tests / forced reopen). */
export function resetDatabaseCache(): void {
  dbPromise = null;
}
